import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { syncAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, PageHeader, useSort } from '../components/UI';

const TABLE_LABELS = {
  radcheck:      'radcheck (check attrs)',
  radreply:      'radreply (reply attrs)',
  radusergroup:  'radusergroup (profile assignment)',
  radgroupcheck: 'radgroupcheck (profile check attrs)',
  radgroupreply: 'radgroupreply (profile reply attrs)',
  nas:           'nas (NAS device)',
};

const CHANGE_STYLE = {
  added:   { color: 'var(--accent-green)',  bg: 'rgba(34,197,94,0.12)' },
  created: { color: 'var(--accent-green)',  bg: 'rgba(34,197,94,0.12)' },
  removed: { color: 'var(--accent-red)',    bg: 'rgba(239,68,68,0.12)' },
  deleted: { color: 'var(--accent-red)',    bg: 'rgba(239,68,68,0.12)' },
  changed: { color: 'var(--accent-orange)', bg: 'rgba(249,115,22,0.12)' },
};

function ChangeLine({ c }) {
  const style = CHANGE_STYLE[c.change] || { color: 'var(--text-muted)', bg: 'var(--bg-input)' };
  const label = c.attribute || c.groupname || c.field;
  const hasBeforeAfter = c.before !== undefined && c.after !== undefined;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, fontFamily: 'var(--font-mono)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', color: style.color, background: style.bg, whiteSpace: 'nowrap' }}>
        {c.change}
      </span>
      {label && label !== '(row)' && <span style={{ color: 'var(--text-secondary)' }}>{label}</span>}
      {c.value !== undefined && <span style={{ color: 'var(--text-muted)' }}>{c.value}</span>}
      {hasBeforeAfter && (<>
        <span style={{ color: 'var(--accent-red)', textDecoration: 'line-through' }}>{String(c.before ?? '∅')}</span>
        <span style={{ color: 'var(--text-muted)' }}>→</span>
        <span style={{ color: 'var(--accent-green)' }}>{String(c.after ?? '∅')}</span>
      </>)}
    </div>
  );
}

function SyncLogDetail({ log }) {
  const details = log.details;
  if (!details || Object.keys(details).length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0' }}>
        {log.message ? log.message : 'Tidak ada detail perubahan row RADIUS DB untuk log ini.'}
      </div>
    );
  }
  return (
    <div style={{ paddingTop: 10, paddingBottom: 6, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {log.message && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{log.message}</div>
      )}
      {Object.entries(details).map(([table, changes]) => (
        <div key={table}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            {TABLE_LABELS[table] || table}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {changes.map((c, i) => <ChangeLine key={i} c={c} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SyncLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [logFilter, setLogFilter] = useState({ status: '', action: '', resource_type: '' });
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const { sort, onSortChange, sortParam } = useSort('created_at', 'desc');

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = { page, per_page: 20, ...logFilter };
      if (sortParam) params.sort = sortParam;
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await syncAPI.logs(params);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { /* logs not critical */ }
    finally { setLogsLoading(false); }
  }, [page, logFilter, sortParam]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const lf = (k, v) => setLogFilter(p => ({ ...p, [k]: v }));

  const logColumns = [
    { key: 'created_at', label: 'Time', sortable: true, render: v => <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? new Date(v).toLocaleString('id-ID', { hour12: false }) : '—'}</span> },
    { key: 'resource_type', label: 'Resource', sortable: true, render: v => <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4 }}>{v}</span> },
    { key: 'resource_id', label: 'ID', sortable: true, render: v => <span className="mono" style={{ fontSize: 12 }}>{v}</span> },
    { key: 'action', label: 'Action', sortable: true, render: v => <span style={{ fontSize: 11, color: 'var(--accent-cyan)' }}>{v}</span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'message', label: 'Message', render: v => v ? <span style={{ fontSize: 11, color: 'var(--accent-red)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }} title={v}>{v}</span> : '—' },
    {
      key: 'id', label: 'Detail', render: (_, log) => {
        const hasDetails = log.details && Object.keys(log.details).length > 0;
        return (
          <span title={hasDetails ? 'Lihat perubahan row RADIUS DB' : 'Tidak ada detail'}>
            <Button size="sm" variant="ghost" disabled={!hasDetails}
              onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
              {expandedId === log.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </Button>
          </span>
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader title="Sync Log" subtitle="Riwayat sinkronisasi tiap resource ke RADIUS server, lengkap dengan detail perubahan row" />

      <Card style={{ padding: 0 }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Sync Logs ({total})</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { key: 'resource_type', opts: ['', 'user', 'nas', 'tenant', 'profile'], label: 'Resource' },
              { key: 'action', opts: ['', 'create', 'update', 'delete', 'migrate', 'sync', 'activate', 'redirect_profile'], label: 'Action' },
              { key: 'status', opts: ['', 'success', 'error'], label: 'Status' },
            ].map(({ key, opts, label }) => (
              <select key={key} value={logFilter[key]} onChange={e => { lf(key, e.target.value); setPage(1); }}
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                <option value="">All {label}</option>
                {opts.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <Button size="sm" variant="ghost" onClick={loadLogs}><RefreshCw size={12} /></Button>
          </div>
        </div>
        <Table columns={logColumns} data={logs} loading={logsLoading} sort={sort} onSortChange={onSortChange} emptyMessage="Belum ada sync log"
          expandedId={expandedId} renderExpanded={log => <SyncLogDetail log={log} />} />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </Card>
    </div>
  );
}
